import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
} from "../../api/apiServices";
import useAuthStatus from "../../hooks/useAuthStatus";

// Notification type → semantic tone, mapped onto the existing token
// scale instead of fixed hex pairs, so icon chips adapt correctly in
// dark mode. Where two types share a "feel" (e.g. deposit/cashback are
// both positive-money events) they intentionally share a tone.
function getNotifMeta(type) {
  const map = {
    deposit:      { icon: "ri-arrow-down-circle-line", tone: "success" },
    buy:          { icon: "ri-arrow-down-line",         tone: "purple"  },
    sell:         { icon: "ri-arrow-up-line",           tone: "danger"  },
    bill_payment: { icon: "ri-receipt-line",            tone: "warning" },
    cashback:     { icon: "ri-percent-line",            tone: "success" },
    milestone:    { icon: "ri-gift-line",               tone: "purple"  },
    streak:       { icon: "ri-fire-line",               tone: "warning" },
    referral:     { icon: "ri-team-line",               tone: "info"    },
    renewal:      { icon: "ri-tv-2-line",               tone: "warning" },
    system:       { icon: "ri-information-line",        tone: "muted"   },
  };
  return map[type] || map.system;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr);
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-NG", { day: "numeric", month: "short" });
}

const FILTERS = [
  { key: "all",          label: "All" },
  { key: "unread",       label: "Unread" },
  { key: "deposit",      label: "Deposits" },
  { key: "buy",          label: "Trades" },
  { key: "bill_payment", label: "Bills" },
  { key: "cashback",     label: "Rewards" },
  { key: "system",       label: "System" },
];

export default function NotificationsPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(false);
  const [page, setPage]                   = useState(1);
  const [hasMore, setHasMore]             = useState(false);
  const [activeFilter, setActiveFilter]   = useState("all");

  const fetchNotifications = useCallback(async (pageNum = 1) => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const res = await getNotifications(pageNum);
      if (res.status) {
        setNotifications(prev => pageNum === 1 ? res.data : [...prev, ...res.data]);
        setUnreadCount(res.unreadCount);
        setHasMore(pageNum < res.pagination.pages);
        setPage(pageNum);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      await markNotificationRead(notif._id).catch(() => {});
      setNotifications(prev => prev.map(n => n._id === notif._id ? { ...n, read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    }
    if (notif.click_action && notif.click_action !== "/") navigate(notif.click_action);
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead().catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    await deleteNotification(id).catch(() => {});
    setNotifications(prev => prev.filter(n => n._id !== id));
  };

  const filtered = notifications.filter(n => {
    if (activeFilter === "unread") return !n.read;
    if (activeFilter === "all")    return true;
    return n.type === activeFilter;
  });

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Header */}
      <div className="bg-surface sticky top-0 z-10 border-b border-border">
        <div className="flex items-center justify-between px-5 pt-14 pb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center hover:bg-surface-hover transition-colors"
            >
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-primary font-bold text-lg font-display">
                Notifications
              </h1>
              {unreadCount > 0 && (
                <span className="bg-brand text-on-brand text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                  {unreadCount}
                </span>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-brand text-xs font-semibold hover:text-brand-hover transition-colors whitespace-nowrap"
            >
              Mark all read
            </button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 px-5 pb-4 overflow-x-auto no-scrollbar">
          {FILTERS.map(f => {
            const active = activeFilter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setActiveFilter(f.key)}
                className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap border ${
                  active
                    ? "bg-brand text-on-brand border-brand"
                    : "bg-surface text-secondary border-border"
                }`}
              >
                {f.label}
                {f.key === "unread" && unreadCount > 0 && (
                  <span className="bg-white/30 text-on-brand rounded-full px-1 text-[10px]">
                    {unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Notifications list */}
      <div className="flex-1 px-5 pt-4 pb-8 space-y-2">
        {loading && notifications.length === 0 ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="bg-surface rounded-2xl px-4 py-3.5 flex items-start gap-3 border border-border">
              <div className="w-10 h-10 rounded-xl bg-surface-hover animate-pulse flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2 pt-1">
                <div className="h-3 w-40 bg-surface-hover rounded-lg animate-pulse" />
                <div className="h-2.5 w-52 bg-surface-hover rounded-lg animate-pulse" />
                <div className="h-2 w-16 bg-surface-hover rounded-lg animate-pulse" />
              </div>
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-16 h-16 rounded-2xl bg-surface-hover flex items-center justify-center">
              <i className="ri-notification-off-line text-faint text-3xl" />
            </div>
            <p className="text-muted text-sm font-medium">
              {activeFilter === "unread" ? "No unread notifications" : "No notifications here"}
            </p>
          </div>
        ) : (
          <>
            {filtered.map(notif => {
              const meta = getNotifMeta(notif.type);
              const isMuted = meta.tone === "muted";
              return (
                <button
                  key={notif._id}
                  onClick={() => handleNotifClick(notif)}
                  className="w-full text-left bg-surface rounded-2xl px-4 py-3.5 flex items-start gap-3 cursor-pointer hover:bg-surface-hover transition-colors border border-border relative group shadow-card"
                >
                  {/* Unread dot */}
                  {!notif.read && (
                    <span className="absolute top-3.5 right-3.5 w-2 h-2 bg-brand rounded-full" />
                  )}

                  {/* Icon */}
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: isMuted ? "var(--color-surface-hover)" : `var(--color-${meta.tone}-light)` }}
                  >
                    <i className={`${meta.icon} text-lg`} style={{ color: isMuted ? "var(--color-text-muted)" : `var(--color-${meta.tone})` }} />
                  </div>

                  {/* Text */}
                  <div className="flex-1 min-w-0 pr-4">
                    <p className={`text-sm font-bold truncate font-display ${notif.read ? "text-secondary" : "text-primary"}`}>
                      {notif.title}
                    </p>
                    <p className="text-muted text-xs leading-relaxed line-clamp-2 mt-0.5">
                      {notif.body}
                    </p>
                    <p className="text-faint text-xs mt-1.5 font-medium">
                      {timeAgo(notif.createdAt)}
                    </p>
                  </div>

                  {/* Delete on hover */}
                  <span
                    onClick={e => handleDelete(e, notif._id)}
                    className="absolute right-3 top-3.5 w-6 h-6 rounded-lg bg-surface-hover hover:bg-danger-light flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <i className="ri-close-line text-muted hover:text-danger text-xs" />
                  </span>
                </button>
              );
            })}

            {hasMore && (
              <button
                onClick={() => fetchNotifications(page + 1)}
                disabled={loading}
                className="w-full py-3 text-xs font-semibold text-brand hover:text-brand-hover transition-colors disabled:opacity-50 text-center"
              >
                {loading ? "Loading…" : "Load more"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
